import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiError } from "@/utils/api";
import { fmtDate, fmt } from "@/utils/format";
import { toast } from "sonner";
import { Plus, Search, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";

const HAREKET_CFG = {
  giris: { label: "Giriş", color: "bg-green-100 text-green-700 border-green-200", Icon: ArrowUp },
  cikis: { label: "Çıkış", color: "bg-red-100 text-red-700 border-red-200", Icon: ArrowDown },
};

const EMPTY_FORM = {
  stok_id: "", tarih: new Date().toISOString().split("T")[0],
  hareket_turu: "giris", miktar: 1, belge_no: "", aciklama: "",
};

export default function StokHareketleri() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [stokSearch, setStokSearch] = useState("");
  const [hareketTuru, setHareketTuru] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // Stok autocomplete state
  const [stokQuery, setStokQuery] = useState("");
  const [stokResults, setStokResults] = useState([]);
  const [selectedStok, setSelectedStok] = useState(null);
  const stokSearchRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 30 };
      if (stokSearch) params.stok_search = stokSearch;
      if (hareketTuru) params.hareket_turu = hareketTuru;
      const r = await api.get("/stok-hareketler", { params });
      setItems(r.data.items);
      setTotal(r.data.total);
      setPages(r.data.pages || 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, stokSearch, hareketTuru]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [stokSearch, hareketTuru, setPage]);

  // Stok autocomplete in modal
  useEffect(() => {
    if (!stokQuery || stokQuery.length < 2) { setStokResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await api.get("/stoklar/", { params: { search: stokQuery, per_page: 8 } });
        setStokResults(r.data.items);
      } catch {
        // autocomplete errors are non-critical; silently ignore
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [stokQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStokSelect = (stok) => {
    setSelectedStok(stok);
    setForm((p) => ({ ...p, stok_id: stok.id }));
    setStokQuery(stok.kod + " — " + stok.ad);
    setStokResults([]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.stok_id) { toast.error("Stok seçilmeli"); return; }
    setSaving(true);
    try {
      await api.post("/stok-hareketler", form);
      toast.success(`Stok hareketi oluşturuldu (${HAREKET_CFG[form.hareket_turu]?.label})`);
      setShowModal(false);
      setForm(EMPTY_FORM);
      setStokQuery("");
      setSelectedStok(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>Stok Hareketleri</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} kayıt</p>
        </div>
        <button onClick={() => { setForm(EMPTY_FORM); setStokQuery(""); setSelectedStok(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-[#0D1B3E] text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#172A58] active:scale-[0.98] transition-all">
          <Plus size={15} /> Yeni Hareket
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={stokSearch} onChange={(e) => setStokSearch(e.target.value)} placeholder="Stok kodu veya adı filtrele..."
            className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E] transition-colors" />
        </div>
        <div className="flex gap-2">
          {[{ v: "", l: "Tümü" }, { v: "giris", l: "Giriş" }, { v: "cikis", l: "Çıkış" }].map(({ v, l }) => (
            <button key={v} onClick={() => setHareketTuru(v)}
              className={`px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${hareketTuru === v ? "bg-[#0D1B3E] text-white border-[#0D1B3E]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="table-container">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr>
                {["Tarih", "Stok Kodu", "Stok Adı", "Hareket Türü", "Miktar", "Belge No", "Açıklama"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Yükleniyor...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Kayıt bulunamadı</td></tr>
              ) : items.map((item) => {
                const cfg = HAREKET_CFG[item.hareket_turu] || { label: item.hareket_turu, color: "bg-slate-100 text-slate-600 border-slate-200" };
                const CfgIcon = cfg.Icon;
                return (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 tabular-nums text-xs text-slate-500">{fmtDate(item.tarih)}</td>
                    <td className="px-4 py-3 font-mono text-xs font-medium text-[#0D1B3E]">{item.stok_kodu}</td>
                    <td className="px-4 py-3 text-slate-700 max-w-[200px] truncate">{item.stok_adi}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${cfg.color}`}>
                        {CfgIcon && <CfgIcon size={11} />}{cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 tabular-nums font-bold text-slate-800">
                      {fmt(item.miktar, 0)} <span className="text-xs font-normal text-slate-400">{item.stok_birim}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{item.belge_no || "-"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-[160px] truncate">{item.aciklama || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <span className="text-xs text-slate-500">{total} kayıt</span>
          <div className="flex items-center gap-1">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40"><ChevronLeft size={15} /></button>
            <span className="text-xs text-slate-600 px-2">{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40"><ChevronRight size={15} /></button>
          </div>
        </div>
      </div>

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-bold text-[#0D1B3E]">Yeni Stok Hareketi</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {/* Stok autocomplete */}
              <div className="relative">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Stok *</label>
                <input ref={stokSearchRef} value={stokQuery}
                  onChange={(e) => setStokQuery(e.target.value)}
                  placeholder="Stok kodu veya adı yazın..."
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E] transition-colors" />
                {stokResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {stokResults.map((s) => (
                      <button key={s.id} type="button" onClick={() => handleStokSelect(s)}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50 border-b border-slate-50 last:border-0">
                        <span className="font-mono text-xs text-slate-500 mr-2">{s.kod}</span>
                        <span className="text-slate-800">{s.ad}</span>
                        <span className={`ml-2 text-xs font-semibold ${(s.stok_miktari ?? 0) > 0 ? "text-green-600" : "text-red-500"}`}>({fmt(s.stok_miktari ?? 0, 0)} {s.birim})</span>
                      </button>
                    ))}
                  </div>
                )}
                {selectedStok && (
                  <p className="text-xs text-green-600 mt-1">Seçildi: {selectedStok.kod} — {selectedStok.ad}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Hareket Türü *</label>
                  <select value={form.hareket_turu} onChange={(e) => setForm((p) => ({ ...p, hareket_turu: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20">
                    <option value="giris">Giriş</option>
                    <option value="cikis">Çıkış</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Miktar *</label>
                  <input type="number" min="0.01" step="0.01" value={form.miktar} required
                    onChange={(e) => setForm((p) => ({ ...p, miktar: parseFloat(e.target.value) || 0 }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Tarih *</label>
                  <input type="date" value={form.tarih} required onChange={(e) => setForm((p) => ({ ...p, tarih: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Belge No</label>
                  <input type="text" value={form.belge_no} onChange={(e) => setForm((p) => ({ ...p, belge_no: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Açıklama</label>
                <input type="text" value={form.aciklama} onChange={(e) => setForm((p) => ({ ...p, aciklama: e.target.value }))}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">İptal</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-md bg-[#0D1B3E] text-white hover:bg-[#172A58] disabled:opacity-60 transition-colors">
                  {saving ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
